import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, TrendingUp, TrendingDown, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { KpiCard } from '@/components/KpiCard';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { getFinanceSummary, getExportUrl } from '@/lib/api';

const COLORS = ['#2BAE9B', '#2A6FDB', '#D97706', '#DC2626', '#16A34A'];
const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function AdminFinancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getFinanceSummary();
        setData(res.data);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm text-slate-500">Finanzdaten werden geladen...</div></div>;
  if (!data || !data.totals) return <EmptyState title="Keine Finanzdaten" description="Noch keine Kampagnen-Events vorhanden." />;

  const { totals, per_client } = data;

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Finanzen" description="Übersicht über Umsatz, Kosten und Margen">
        <Button onClick={() => window.open(getExportUrl('finance'), '_blank')} variant="outline" className="btn-lift" data-testid="finance-export-csv">
          <Download className="w-4 h-4 mr-2" /> CSV Export
        </Button>
      </PageHeader>

      <motion.div variants={fadeIn} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <KpiCard label="Gesamt Umsatz" value={totals.total_revenue} prefix="€" testId="finance-revenue" />
        <KpiCard label="Gesamt Spend" value={totals.total_spend} prefix="€" testId="finance-spend" />
        <KpiCard label="Marge" value={totals.total_margin} prefix="€" testId="finance-margin" />
        <KpiCard label="Installs" value={totals.total_installs} testId="finance-installs" />
      </motion.div>

      {/* Client Revenue Chart */}
      <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 mb-6">
        <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Umsatz pro Kunde</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={per_client}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="client_name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
            <Bar dataKey="total_revenue" name="Revenue (€)" radius={[4, 4, 0, 0]}>
              {per_client?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Per Client Table */}
      <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-display text-base font-semibold text-slate-900">Finanzübersicht pro Kunde</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="finance-table">
            <thead><tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left p-3 pl-4 font-medium text-slate-500">Kunde</th>
              <th className="text-right p-3 font-medium text-slate-500">Revenue (€)</th>
              <th className="text-right p-3 font-medium text-slate-500">Spend (€)</th>
              <th className="text-right p-3 font-medium text-slate-500">Marge (€)</th>
              <th className="text-right p-3 font-medium text-slate-500">Marge %</th>
              <th className="text-right p-3 pr-4 font-medium text-slate-500">Installs</th>
            </tr></thead>
            <tbody>
              {per_client?.map((cl, i) => (
                <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                  <td className="p-3 pl-4 font-medium text-slate-900">{cl.client_name}</td>
                  <td className="p-3 text-right tabular-nums text-slate-700">{cl.total_revenue?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right tabular-nums text-slate-700">{cl.total_spend?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right tabular-nums">
                    <span className={cl.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                      {cl.margin >= 0 ? '+' : ''}{cl.margin?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      cl.margin_pct >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {cl.margin_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {cl.margin_pct?.toFixed(1)}%
                    </span>
                  </td>
                  <td className="p-3 pr-4 text-right tabular-nums text-slate-700">{cl.total_installs?.toLocaleString('de-DE')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}
